import { alpha, type SxProps, type Theme } from "@mui/material/styles";

import { customShadows } from "@/theme/customTokens";

export const root: SxProps<Theme> = {
  position: "relative",
  isolation: "isolate",
  minHeight: "100dvh",
  width: "100%",
  alignItems: "center",
  justifyContent: "center",
  overflow: "hidden",
  bgcolor: "primary.dark",
  px: { xs: 2, sm: 3 },
  py: { xs: 3, sm: 6 },
  boxSizing: "border-box",
};

export const backgroundImage: SxProps<Theme> = {
  position: "absolute",
  inset: 0,
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "center",
  pointerEvents: "none",
  userSelect: "none",
  zIndex: 0,
};

export const overlay = (theme: Theme): SxProps<Theme> => ({
  position: "absolute",
  inset: 0,
  zIndex: 0,
  pointerEvents: "none",
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.62)}, ${alpha(theme.palette.primary.main, 0.48)})`,
});

export const container: SxProps<Theme> = {
  position: "relative",
  zIndex: 1,
  width: "100%",
  maxWidth: 440,
};

export const paper =
  (theme: Theme): SxProps<Theme> =>
  ({
    display: "flex",
    flexDirection: "column",
    pt: { xs: 3, sm: 5 },
    px: { xs: 3, sm: 4.75 },
    pb: { xs: 2.5, sm: 4.25 },
    overflow: "hidden",
    position: "relative",
    bgcolor: "background.paper",
    borderRadius: 3,
    border: `1px solid ${theme.palette.divider}`,
    boxShadow: customShadows.authCard,
  });

export const backLink: SxProps<Theme> = {
  flexDirection: "row",
  alignItems: "center",
  gap: 0.75,
  textDecoration: "none",
  color: "text.secondary",
  mb: 2.25,
  width: "fit-content",
  "&:hover": {
    color: "text.primary",
  },
};

export const header =
  (spacing: "default" | "spacious"): SxProps<Theme> =>
  ({
    alignItems: "center",
    gap: 2.25,
    mb: spacing === "spacious" ? { xs: 2.75, sm: 3.75 } : { xs: 2.5, sm: 3.25 },
  });

export const logo: SxProps<Theme> = {
  height: { xs: 60, sm: 74 },
  width: "auto",
  objectFit: "contain",
};

export const headerIconWrap = (theme: Theme): SxProps<Theme> => ({
  width: 68,
  height: 68,
  borderRadius: "50%",
  bgcolor: alpha(theme.palette.primary.main, 0.1),
  alignItems: "center",
  justifyContent: "center",
  color: "primary.main",
});

export const titleBlock: SxProps<Theme> = {
  textAlign: "center",
};

export const title: SxProps<Theme> = {
  color: "primary.dark",
  fontWeight: 700,
  fontSize: { xs: "1.125rem", sm: "1.3125rem" },
};

export const subtitle =
  (spacing: "default" | "spacious"): SxProps<Theme> =>
  ({
    color: "text.secondary",
    mt: spacing === "spacious" ? 1 : 0.75,
    lineHeight: 1.8,
    fontSize: { xs: "0.8125rem", sm: "0.84375rem" },
  });

export const copyright = (theme: Theme): SxProps<Theme> => ({
  position: "absolute",
  bottom: { xs: 16, sm: 22 },
  insetInlineStart: 0,
  width: "100%",
  textAlign: "center",
  fontSize: "0.75rem",
  color: alpha(theme.palette.common.white, 0.85),
  zIndex: 1,
  pointerEvents: "none",
});

export const footerLinkRow: SxProps<Theme> = {
  flexDirection: "row",
  justifyContent: "center",
  alignItems: "center",
  gap: 0.5,
  mt: 2.75,
};
